import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';
import invariant from 'tiny-invariant';

import type { Instruction } from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/hitbox';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import * as liveRegion from '@atlaskit/pragmatic-drag-and-drop-live-region';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';

import type { CustomizationDataShapes, HasMenuIdAndVisible } from '../../common/types';
import type { ElementRegistry } from '../element-registry';

import { messages } from './messages';

export interface DragAndDropAction {
	draggedMenuItemId: string;
	draggedMenuItemLabel?: string;
	droppedOnMenuItemId?: string;
	instruction: Instruction;
	trigger: 'pointer' | 'keyboard';
	type:
		| 'item-reorder'
		| 'shortcut-reorder'
		| 'item-hide'
		| 'shortcut-hide'
		| 'item-unhide'
		| 'shortcut-unhide';
}

type DragAndDropActionExtended =
	| ReorderDragAndDropAction
	| HideDragAndDropAction
	| UnhideDragAndDropAction;

interface ReorderDragAndDropAction extends DragAndDropAction {
	finishIndex: number;
	startIndex: number;
	type: 'item-reorder' | 'shortcut-reorder';
}

interface HideDragAndDropAction extends DragAndDropAction {
	startIndex: number;
	type: 'item-hide' | 'shortcut-hide';
	// finish is the More menu
}

interface UnhideDragAndDropAction extends DragAndDropAction {
	finishIndex: number;
	type: 'item-unhide' | 'shortcut-unhide';
	// start is the More menu
}

interface SideNavMenuData extends CustomizationDataShapes {
	setUserMenuData: (data: HasMenuIdAndVisible[]) => Promise<any>;
}

export function useDragAndDropSave(
	sideNavMenuData: SideNavMenuData,
	elementRegistry: ElementRegistry,
) {
	/*
	Get current order of items and shortcuts
	Apply reorder or hide operation
	Save the updated data using onSave
	*/
	const intl = useIntl();
	const [lastDragAndDropAction, setLastDragAndDropAction] =
		useState<DragAndDropActionExtended | null>(null);

	// Clean up live region when unmounted
	useEffect(() => {
		return () => liveRegion.cleanup();
	}, []);

	const dragAndDropSave = useCallback(
		async (action: DragAndDropAction) => {
			// Item reordering
			if (isItemReorderAction(action)) {
				invariant(action.droppedOnMenuItemId, 'item-reorder action must have droppedOnMenuItemId');

				const startIndex = sideNavMenuData.customizationData.findIndex(
					(datum) => datum.menuId === action.draggedMenuItemId,
				);
				const itemsAndShortcutsIdsWithoutDraggedItem = sideNavMenuData.customizationData
					.map((datum) => datum.menuId)
					.filter((id) => id !== action.draggedMenuItemId);
				const dropTargetIndex = itemsAndShortcutsIdsWithoutDraggedItem.indexOf(
					action.droppedOnMenuItemId,
				);

				if (startIndex === -1) {
					// TODO: NAVX-1289 Track this error case somehow - maybe analytics
					return;
				}

				if (dropTargetIndex === -1) {
					// TODO: NAVX-1289 Track this error case somehow - maybe analytics
					return;
				}

				const finishIndex =
					action.instruction.operation === 'reorder-after' ? dropTargetIndex + 1 : dropTargetIndex;

				const visibleStartIndex = sideNavMenuData.visibleItemMenuIds.indexOf(
					action.draggedMenuItemId,
				);
				const visibleDropTargetIndex = sideNavMenuData.visibleItemMenuIds
					.filter((id) => id !== action.draggedMenuItemId)
					.indexOf(action.droppedOnMenuItemId);
				if (visibleStartIndex === -1) {
					// TODO: NAVX-1289 Track this error case somehow - maybe analytics
					return;
				}

				if (visibleDropTargetIndex === -1) {
					// TODO: NAVX-1289 Track this error case somehow - maybe analytics
					return;
				}
				const visibleFinishIndex =
					action.instruction.operation === 'reorder-after'
						? visibleDropTargetIndex + 1
						: visibleDropTargetIndex;

				const reordered = reorder({
					list: sideNavMenuData.customizationData,
					startIndex,
					finishIndex,
				});
				await sideNavMenuData.setUserMenuData(reordered);
				setLastDragAndDropAction({
					...action,
					startIndex: visibleStartIndex,
					finishIndex: visibleFinishIndex,
				});
				return;
			}

			// Item hiding into More menu
			if (isItemHideAction(action)) {
				const dataWithHiddenItem = sideNavMenuData.customizationData.map((datum) => {
					if (datum.menuId === action.draggedMenuItemId) {
						return {
							...datum,
							visible: false,
						};
					}
					return datum;
				});
				await sideNavMenuData.setUserMenuData(dataWithHiddenItem);
				setLastDragAndDropAction(action);

				return;
			}

			// Shortcut reordering
			if (isShortcutReorderAction(action)) {
				// TODO: NAVX-1004 Implement shortcut reordering and hiding logic
				return;
			}

			// Shortcut hiding into More menu
			if (isShortcutHideAction(action)) {
				// TODO: NAVX-1004 Implement shortcut reordering and hiding logic
				return;
			}
		},
		[sideNavMenuData],
	);

	// After action has been performed, announce & flash the item
	useEffect(() => {
		if (!lastDragAndDropAction) {
			return;
		}
		if (isItemReorderAction(lastDragAndDropAction)) {
			const label =
				lastDragAndDropAction.draggedMenuItemLabel || lastDragAndDropAction.draggedMenuItemId;

			const element = elementRegistry.getL1ItemElement(lastDragAndDropAction.draggedMenuItemId);
			if (element) {
				// Flash the moved item
				triggerPostMoveFlash(element);

				// Focus the moved item if move was triggered by keyboard
				if (lastDragAndDropAction.trigger === 'keyboard') {
					// Per Atlaskit team, .focus() seems to be more reliable in the next task when used with VoiceOver
					setTimeout(() => element.focus());
				}

				// Announce the move for assistive technology users
				liveRegion.announce(
					intl.formatMessage(messages.a11yAnnouncementItemReordered, {
						label,
						startIndex: lastDragAndDropAction.startIndex + 1,
						finishIndex: lastDragAndDropAction.finishIndex + 1,
					}),
				);
			}
			return;
		}

		if (isItemHideAction(lastDragAndDropAction)) {
			const label =
				lastDragAndDropAction.draggedMenuItemLabel || lastDragAndDropAction.draggedMenuItemId;

			const moreMenuElement = elementRegistry.getMoreMenuElement();
			if (moreMenuElement) {
				// Flash the More menu
				triggerPostMoveFlash(moreMenuElement);

				// Focus the More menu if move was triggered by keyboard
				if (lastDragAndDropAction.trigger === 'keyboard') {
					// Per Atlaskit team, .focus() seems to be more reliable in the next task when used with VoiceOver
					setTimeout(() => moreMenuElement.focus());
				}

				// Announce the hide for assistive technology users
				liveRegion.announce(
					intl.formatMessage(messages.a11yAnnouncementItemHidden, {
						label,
					}),
				);
			}
		}
	}, [elementRegistry, intl, lastDragAndDropAction]);

	return {
		dragAndDropSave,
	};
}

function isItemReorderAction(action: DragAndDropAction): action is ReorderDragAndDropAction {
	return action.type === 'item-reorder';
}

function isItemHideAction(action: DragAndDropAction): action is HideDragAndDropAction {
	return action.type === 'item-hide';
}

function isShortcutReorderAction(action: DragAndDropAction): action is ReorderDragAndDropAction {
	return action.type === 'shortcut-reorder';
}

function isShortcutHideAction(action: DragAndDropAction): action is HideDragAndDropAction {
	return action.type === 'shortcut-hide';
}
